import {hierarchy, pack} from "d3-hierarchy";

import {assign, elem} from "@d3plus/dom";
import {nestGroups} from "@d3plus/data";
import {accessor, configPrep, constant} from "../utils/index.js";
import {Circle} from "../shapes/index.js";
import Viz from "./Viz.js";

const recursionCircles = (
  d: Record<string, unknown>,
  arr: Record<string, unknown>[] = [],
) => {
  if (d.values) {
    (d.values as Record<string, unknown>[]).forEach(h => {
      arr.push(h);
      recursionCircles(h, arr);
    });
  } else {
    arr.push(d);
  }
  return arr;
};

/**
    Uses the [d3 pack layout](https://github.com/d3/d3-hierarchy#pack) to creates Circle Packing chart based on an array of data.
*/
export default class Pack extends Viz {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;

  /**
      Invoked when creating a new class instance, and sets any default parameters.
      @private
*/
  constructor() {
    super();

    this._layoutPadding = 1;

    const defaultLegend = this._legend;
    this._legend = (config: any, arr: any) => {
      if (arr.length === this._filteredData.length) return false;
      return defaultLegend.bind(this)(config, arr);
    };

    this._on.mouseenter = () => {};

    const defaultMouseMoveLegend = this._on["mousemove.legend"];
    this._on["mousemove.legend"] = (d: any, i: any, x: any, event: any) => {
      defaultMouseMoveLegend(d, i, x, event);

      const ids = this._ids(d, i);
      const hoverData = recursionCircles(d);

      this.hover((h: any) => {
        const hover = Object.keys(h)
          .filter(key => key !== "value")
          .every(key => d[key] && d[key].includes(h[key]));

        if (hover) hoverData.push(h);
        else if (ids.includes(h.key))
          hoverData.push(...recursionCircles(h, [h]));

        return hoverData.includes(h);
      });
    };
    const defaultMouseMoveShape = this._on["mousemove.shape"];
    this._on["mousemove.shape"] = (d: any, i: any, x: any, event: any) => {
      if (d.__d3plusTooltip__) defaultMouseMoveShape(d, i, x, event);
      const hoverData = recursionCircles(d, [d]);
      this.hover((h: any) => hoverData.includes(h));
    };

    this._pack = pack();
    this._packOpacity = constant(0.25);
    this._shape = constant("Circle");
    this._shapeConfig = assign(this._shapeConfig, {
      Circle: {
        label: (d: any) => (d.parent && !d.children ? d.id : false),
        labelConfig: {
          fontResize: true,
        },
        opacity: (d: any) => d.__d3plusOpacity__,
      },
    });
    this._sort = (a: any, b: any) => b.value - a.value;
    this._sum = accessor("value");
  }

  /**
      Extends the draw behavior of the abstract Viz class.
      @private
*/
  _draw(callback?: () => void) {
    (super._draw as (...args: unknown[]) => unknown)(callback);

    const height = this._height - this._margin.top - this._margin.bottom,
      width = this._width - this._margin.left - this._margin.right;

    const diameter = Math.min(height, width);
    const transform = `translate(${(width - diameter) / 2}, ${(height - diameter) / 2})`;

    const nestedData = nestGroups(
      this._filteredData,
      this._groupBy.slice(0, this._drawDepth + 1),
    );

    const packData = this._pack
      .padding(this._layoutPadding)
      .size([diameter, diameter])(
        hierarchy(
          {key: (nestedData as unknown as Record<string, unknown>).key, values: nestedData} as Record<
            string,
            unknown
          >,
          (d: Record<string, unknown>) => d.values as Record<string, unknown>[],
        )
          .sum(this._sum)
          .sort(this._sort),
      )
      .descendants()
      .filter((d: any, i: any) => {
        d.__d3plus__ = true;
        d.i = i;
        d.id = d.parent ? d.parent.data.key : "root";
        d.data.__d3plusOpacity__ = d.height ? this._packOpacity(d.data, i) : 1;
        d.data.__d3plusTooltip__ = !d.height ? true : false;
        return !d.children || d.children.length > 1;
      });

    this._shapes.push(
      new Circle()
        .data(packData)
        .select(
          elem("g.d3plus-Pack", {
            parent: this._select,
            enter: {transform},
            update: {transform},
          }).node(),
        )
        .config((configPrep as any).bind(this as any)(this._shapeConfig, "shape", "Circle"))
        .render(),
    );

    return this;
  }

  /**
      The hover callback function for highlighting shapes on mouseover.
*/
  hover(_: any) {
    this._hover = _;
    this._shapes.forEach((s: any) => s.hover(_));

    if (this._legend) this._legendClass.hover(_);
    return this;
  }

  /**
      The inner and outer padding for the pack layout.
*/
  layoutPadding(_: any) {
    return arguments.length
      ? ((this._layoutPadding = _), this)
      : this._layoutPadding;
  }

  /**
      The opacity of nested circles within the pack layout.
*/
  packOpacity(_: any) {
    return arguments.length
      ? ((this._packOpacity = typeof _ === "function" ? _ : constant(_)), this)
      : this._packOpacity;
  }

  /**
      Sort comparator function for the pack layout. Defaults to descending order by the associated input data's numeric value attribute.

@example
function comparator(a, b) {
  return b.value - a.value;
}
*/
  sort(_: any) {
    return arguments.length ? ((this._sort = _), this) : this._sort;
  }

  /**
      The sum accessor used for sizing each circle in the pack layout.

@example
function sum(d) {
  return d.sum;
}
*/
  sum(_: any) {
    return arguments.length
      ? ((this._sum = typeof _ === "function" ? _ : accessor(_)), this)
      : this._sum;
  }
}
